import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User, Wallet, Lock, LogOut, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useState } from 'react';
import { toast } from 'sonner';

interface SettingsLayoutProps {
  children: ReactNode;
}

const navItems = [
  { id: 'profile', label: 'Profile', icon: User, path: '/profile' },
  { id: 'accounts', label: 'Linked Accounts', icon: Wallet, path: '/accounts' },
  { id: 'privacy', label: 'Privacy & Security', icon: Lock, path: '/privacy' },
];

export function SettingsLayout({ children }: SettingsLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const currentPath = location.pathname;

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      navigate('/auth');
    } catch {
      toast.error('Failed to sign out');
      setIsSigningOut(false);
    }
  };

  return (
    <div className="flex gap-6 min-h-[calc(100vh-200px)]">
      {/* Left Sidebar */}
      <aside className="w-64 shrink-0 hidden md:block">
        <div className="sticky top-24 space-y-2">
          <h2 className="text-lg font-semibold mb-4 px-3">Settings</h2>

          <nav className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPath === item.path;

              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </button>
              );
            })}
          </nav>

          <div className="pt-4 mt-4 border-t">
            <Button
              variant="ghost"
              className={cn(
                'w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10'
              )}
              onClick={handleSignOut}
              disabled={isSigningOut}
            >
              {isSigningOut ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing out...
                </>
              ) : (
                <>
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </>
              )}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {children}
      </main>
    </div>
  );
}
